import {
  AssignmentDeviationEnum,
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  CaseTypeEnum,
  ExpectedExpenseDto,
  ExpenseType,
  StandardizedCommentDto,
  UserModel,
  ZipCodeArea,
} from 'api/model';
import { useCallback, useMemo, useState } from 'react';
import { isSameSwedishDay, toInputDateTimeString } from 'utils/date-helpers';
import { isBreakOnTopForLeg } from 'utils/breakOnTopPlanning';
import { BasicFormState, ValidationResults } from 'hooks/useForm';
import {
  caseTypeDefaultsToSendsCustomerUpdateMessages,
  isRmOtherLikeCaseType,
} from '../CaseAssignments/utils';
import { AssignmentField, isBookedToChanged } from './types';
import { bookedToChangeReasonRequired } from 'constants/BookedToChangeReasons';
import { createValidations } from './caseAssignmentFormsValidators';
import { CompanyCoordinateResolver } from './useCompanyCoordinateResolver';
import { getElectricCarBufferMs } from 'utils/electricCarBuffer';
import { fixedTimeToHoursInput } from './fixedTimeInput';

/**
 * Extra kontext som formuläret (men inte spar-vägen) kan skicka in för att få
 * laddbufferten och koordinaterna uträknade LIVE i stället för att bara ärva serverns
 * persisterade värden. Utan den beter sig konverteringen exakt som förut.
 */
export interface AssignmentFormConversionContext {
  /** Klientspegel av backends koordinatresolvning — företagens sparade geotaggar. */
  resolveCompanyCoordinates?: CompanyCoordinateResolver;
  /** Ärendets Elbil-flagga. Bor på ärendet (`Case.isElectric`), inte på uppdraget. */
  isElectricCase?: boolean;
  /**
   * Elbil-flaggan på det sparade ärendet. Är den oförändrad (och uppdragets adresser, län och
   * typ likaså) visas det sparade — ev. TL-valda — buffertvärdet i stället för regeln.
   */
  savedIsElectricCase?: boolean;
}

export const convertStringToExpectedExpensesArray = (
  expectedExpensesString?: string
): ExpectedExpenseDto[] =>
  (expectedExpensesString ?? '').split(',').reduce((acc, cur) => {
    if (cur === '') {
      return acc;
    }
    const type = Number(cur) as ExpenseType;
    return [
      ...acc,
      {
        assignmentId: -1,
        type,
        estimatedCost: 0,
      } satisfies ExpectedExpenseDto,
    ];
  }, [] as ExpectedExpenseDto[]);

export interface NewAssignmentOverrides {
  firstPossibleStartTime?: Date;
  lastPossibleEndTime?: Date;
  isUsersFirstAssignmentOfDay?: boolean;
  isUsersLastAssignmentOfDay?: boolean;
}

export const hourInMilliseconds = 1000 * 60 * 60;
export const minuteInMilliseconds = 1000 * 60;

export function getDefaultAssignment(): AssignmentModel {
  return {
    caseID: -1,
    assignmentID: -1,
    publicToken: undefined,
    assignmentDeviationID: AssignmentDeviationEnum.None,
    assignmentcancelTypeID: undefined,
    assignmentTypeID: AssignmentTypeEnum.Normal,
    sendCustomerUpdateMessages: true,
    timestamp: '',
    assignedTo: undefined,
    assignmentStatusID: AssignmentStatusEnum.Created,
    fromName: '',
    fromAddress: '',
    fromCity: '',
    fromZip: '',
    fromCounty: undefined,
    toName: '',
    toAddress: '',
    toCity: '',
    toZip: '',
    toCounty: undefined,
    bookedTo: new Date(),
    startTime: undefined,
    endTime: undefined,
    fixedAssignmentTime: undefined,
    breakTime: 0,
    autoApprove: true,
    extraInfo: '',
    inspectionApproved: undefined,
    keyHandedIn: undefined,
    shouldGoOnTruck: false,
    assignmentTimeDisplay: '',
    workedTimeDisplay: '',
    expenseSum: 0,
    circleKExpenseSum: 0,
    estimatedDuration: undefined,
    estimatedLoadingTime: undefined,
    estimatedUnloadingTime: undefined,
    estimatedStartTime: undefined,
    systemEstimatedDurationExact: undefined,
    systemEstimatedDurationApprox: undefined,
    expectedExpenses: [],
    standardizedComments: [],
    standardizedCommentSuggestions: [],
    allowPrivateStopOver: false,
    hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime: false,
    assignedToRating: undefined,
    assignedToRatingReason: undefined,
    standByAssignmentTime: undefined,
    standByAssignmentIsEligible: undefined,
    standByAssignmentTimeReason: undefined,
    assigneeShouldBookTheirOwnTicket: false,
    ticketInfo: '',
    stayOverTypeID: undefined,
    stayOverInfo: undefined,
  } as unknown as AssignmentModel;
}

export function initializeAssignmentForm(
  assignment: AssignmentModel | null,
  currentCaseType: CaseTypeEnum,
  defaultAssignmentType?: AssignmentTypeEnum
): BasicFormState<AssignmentField> {
  if (assignment) {
    return {
      [AssignmentField.assignmentDeviationID]: String(
        assignment.assignmentDeviationID ?? AssignmentDeviationEnum.None
      ),
      [AssignmentField.assignmentCancelTypeID]: String(
        assignment.assignmentCancelTypeID ?? ''
      ),
      [AssignmentField.assignmentTypeID]: String(assignment.assignmentTypeID),
      [AssignmentField.assignmentStatusID]: String(
        assignment.assignmentStatusID
      ),
      [AssignmentField.sendCustomerUpdateMessages]:
        assignment.sendCustomerUpdateMessages ? 'true' : 'false',

      [AssignmentField.fromName]: assignment.fromName,
      [AssignmentField.fromAddress]: assignment.fromAddress,
      [AssignmentField.fromCity]: assignment.fromCity,
      [AssignmentField.fromZip]: assignment.fromZip,
      [AssignmentField.fromCounty]: assignment.fromCounty?.id.toString() ?? '',

      [AssignmentField.toName]: assignment.toName,
      [AssignmentField.toAddress]: assignment.toAddress,
      [AssignmentField.toCity]: assignment.toCity,
      [AssignmentField.toZip]: assignment.toZip,
      [AssignmentField.toCounty]: assignment.toCounty?.id.toString() ?? '',

      [AssignmentField.bookedTo]: toInputDateTimeString(assignment.bookedTo),
      [AssignmentField.startTime]: assignment.startTime
        ? toInputDateTimeString(assignment.startTime)
        : '',
      [AssignmentField.endTime]: assignment.endTime
        ? toInputDateTimeString(assignment.endTime)
        : '',
      [AssignmentField.fixedAssignmentTimeInHours]: fixedTimeToHoursInput(
        assignment.fixedAssignmentTime
      ),
      [AssignmentField.breakTime]: String(assignment.breakTime ?? 0),
      [AssignmentField.extraInfo]: assignment.extraInfo,

      [AssignmentField.assignedTo]: String(assignment.assignedTo?.userID ?? ''),

      // From the planning rollout date onward, regular assignments STORE EstimatedDuration as the occupied
      // window (drive + break). The picker edits the pure DRIVE estimate, so on load we subtract the break.
      // Before the rollout (by BookedTo), for fixed-time (separate fixedAssignmentTime field) and for passenger
      // legs (break taken during the trip, inside the leg's time), the stored value is the drive/window as-is
      // and is left alone. The inverse (+break) is applied on save below.
      [AssignmentField.estimatedDuration]:
        assignment.estimatedDuration !== undefined &&
        assignment.estimatedDuration !== null
          ? String(
              !assignment.fixedAssignmentTime &&
                isBreakOnTopForLeg(
                  assignment.bookedTo,
                  assignment.assignmentTypeID,
                  currentCaseType
                )
                ? Math.max(
                    0,
                    assignment.estimatedDuration -
                      Math.round((assignment.breakTime ?? 0) * 60 * 1000)
                  )
                : assignment.estimatedDuration
            )
          : '',
      [AssignmentField.estimatedLoadingTime]:
        assignment.estimatedLoadingTime !== undefined &&
        assignment.estimatedLoadingTime !== null
          ? String(assignment.estimatedLoadingTime)
          : '',
      [AssignmentField.estimatedUnloadingTime]:
        assignment.estimatedUnloadingTime !== undefined &&
        assignment.estimatedUnloadingTime !== null
          ? String(assignment.estimatedUnloadingTime)
          : '',
      [AssignmentField.estimatedStartTime]: assignment.estimatedStartTime
        ? toInputDateTimeString(assignment.estimatedStartTime)
        : '',
      [AssignmentField.systemEstimatedDurationExact]:
        assignment.systemEstimatedDurationExact !== undefined &&
        assignment.systemEstimatedDurationExact !== null
          ? String(assignment.systemEstimatedDurationExact)
          : '',
      [AssignmentField.systemEstimatedDurationApprox]:
        assignment.systemEstimatedDurationApprox !== undefined &&
        assignment.systemEstimatedDurationApprox !== null
          ? String(assignment.systemEstimatedDurationApprox)
          : '',

      [AssignmentField.expectedExpenses]: (
        assignment.expectedExpenses ?? []
      ).reduce(
        (acc, cur) => {
          if (acc === '') {
            return `${Number(cur.type)}`;
          }
          return `${acc},${Number(cur.type)}`;
        },
        ''
      ),

      [AssignmentField.standardizedComments]: JSON.stringify(
        assignment.standardizedComments ?? []
      ),

      [AssignmentField.allowPrivateStopOver]: assignment.allowPrivateStopOver
        ? 'true'
        : 'false',

      // Nullable trestatus — '' = ej angivet (samma mönster som standByAssignmentIsEligible).
      [AssignmentField.keyHandedIn]:
        assignment.keyHandedIn === true
          ? 'true'
          : assignment.keyHandedIn === false
            ? 'false'
            : '',

      // "Ska på lastbil" per ben — vanlig boolean (ingen trestatus).
      [AssignmentField.shouldGoOnTruck]: assignment.shouldGoOnTruck
        ? 'true'
        : 'false',

      [AssignmentField.hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime]:
        'false',

      [AssignmentField.assignedToRating]:
        assignment.assignedToRating !== undefined &&
        assignment.assignedToRating !== null
          ? assignment.assignedToRating.toString()
          : '',
      [AssignmentField.assignedToRatingReason]:
        assignment.assignedToRatingReason !== undefined &&
        assignment.assignedToRatingReason !== null
          ? assignment.assignedToRatingReason
          : '',
      [AssignmentField.standByAssignmentTimeInHours]:
        assignment.standByAssignmentTime !== undefined &&
        assignment.standByAssignmentTime !== null
          ? (
              Math.round(
                assignment.standByAssignmentTime / (hourInMilliseconds / 100)
              ) / 100
            ).toString()
          : '',
      [AssignmentField.standByAssignmentIsEligible]:
        assignment.isStandbyEligible === true
          ? 'true'
          : assignment.isStandbyEligible === false
            ? 'false'
            : '',
      [AssignmentField.standByAssignmentTimeReason]:
        assignment.standbyAssignmentTimeReason ?? '',
      [AssignmentField.assigneeShouldBookTheirOwnTicket]:
        assignment.assigneeShouldBookTheirOwnTicket === true ? 'true' : 'false',
      [AssignmentField.ticketInfo]: assignment.ticketInfo ?? '',
      [AssignmentField.firstPossibleStartTime]: '',
      [AssignmentField.lastPossibleEndTime]: '',
      [AssignmentField.fromAddressIsOutSideHomeArea]: 'false',
      [AssignmentField.toAddressIsOutSideHomeArea]: 'false',
      [AssignmentField.isNewAssignment]:
        assignment.assignmentID === -1 ? 'true' : 'false',
      [AssignmentField.isUsersFirstAssignmentOfDay]: 'false',
      [AssignmentField.isUsersLastAssignmentOfDay]: 'false',
      [AssignmentField.isUserBetweenAssignments]: 'false',
      [AssignmentField.stayOverTypeID]: String(assignment.stayOverTypeID ?? ''),
      [AssignmentField.stayOverInfo]: assignment.stayOverInfo ?? '',
      [AssignmentField.ridesAlongWithAssignmentID]: String(
        assignment.ridesAlongWithAssignmentID ?? ''
      ),
      [AssignmentField.ridesAlongExemptionReason]: '',
      [AssignmentField.runnerCustomerLegOverrideReason]: '',
      [AssignmentField.bookedToChangeReason]: '',
      [AssignmentField.bookedToChangeComment]: '',
      [AssignmentField.originalBookedTo]:
        assignment.assignmentID === -1
          ? ''
          : toInputDateTimeString(assignment.bookedTo),
      // Bara val gjorda under redigeringen — det sparade värdet läses ur basmodellen.
      [AssignmentField.manualElectricCarBufferTime]: '',
      // Ny, eller ännu inte planerad (t.ex. en kopia från "Kopiera över flera dagar").
      [AssignmentField.ridesAlongRequired]:
        currentCaseType === CaseTypeEnum.Samakning &&
        (assignment.assignmentID === -1 || !assignment.assignedTo)
          ? 'true'
          : 'false',

      // stopovers
      [AssignmentField.addingStopOver]: 'false',
      [AssignmentField.stopOverName]: '',
      [AssignmentField.stopOverAddress]: '',
      [AssignmentField.stopOverCity]: '',
      [AssignmentField.stopOverZip]: '',
      [AssignmentField.stopOverCounty]: '',
      [AssignmentField.stopOverEstimatedDuration]: '',
      [AssignmentField.stopOverFixedAssignmentTimeInHours]: '',

      // personal transportation to assignment
      [AssignmentField.addingPersonalTransportationToAssignment]: 'false',
      [AssignmentField.personalTransportationToAssignmentEstimatedDuration]: '',
      [AssignmentField.personalTransportationToAssignmentEstimatedStartTime]:
        '',
      [AssignmentField.personalTransportationToAssignmentFixedAssignmentTimeInHours]:
        '',
      [AssignmentField.personalTransportationToAssignmentName]: '',
      [AssignmentField.personalTransportationToAssignmentAddress]: '',
      [AssignmentField.personalTransportationToAssignmentCity]: '',
      [AssignmentField.personalTransportationToAssignmentZip]: '',
      [AssignmentField.personalTransportationToAssignmentCounty]: '',
      [AssignmentField.personalTransportationToAssignmentAssignmentTypeID]:
        AssignmentTypeEnum.GoByPublicTransport.toString(),
      [AssignmentField.personalTransportationToAssigneeShouldBookTheirOwnTicket]:
        'false',
      [AssignmentField.personalTransportationToTicketInfo]: '',
      [AssignmentField.personalTransportationToExpectedExpenses]: '',

      // personal transportation from assignment
      [AssignmentField.addingPersonalTransportationFromAssignment]: 'false',
      [AssignmentField.personalTransportationFromAssignmentEstimatedDuration]:
        '',
      [AssignmentField.personalTransportationFromAssignmentEstimatedStartTime]:
        '',
      [AssignmentField.personalTransportationFromAssignmentFixedAssignmentTimeInHours]:
        '',
      [AssignmentField.personalTransportationFromAssignmentName]: '',
      [AssignmentField.personalTransportationFromAssignmentAddress]: '',
      [AssignmentField.personalTransportationFromAssignmentCity]: '',
      [AssignmentField.personalTransportationFromAssignmentZip]: '',
      [AssignmentField.personalTransportationFromAssignmentCounty]: '',
      [AssignmentField.personalTransportationFromAssignmentAssignmentTypeID]:
        AssignmentTypeEnum.GoByPublicTransport.toString(),

      [AssignmentField.personalTransportationFromAssigneeShouldBookTheirOwnTicket]:
        'false',
      [AssignmentField.personalTransportationFromTicketInfo]: '',
      [AssignmentField.personalTransportationFromExpectedExpenses]: '',
    };
  }

  return {
    [AssignmentField.assignmentDeviationID]: String(
      AssignmentDeviationEnum.None
    ),
    [AssignmentField.assignmentCancelTypeID]: '',
    [AssignmentField.assignmentTypeID]: String(
      defaultAssignmentType ?? AssignmentTypeEnum.Normal
    ),
    [AssignmentField.assignmentStatusID]: String(AssignmentStatusEnum.Created),
    [AssignmentField.sendCustomerUpdateMessages]:
      caseTypeDefaultsToSendsCustomerUpdateMessages(currentCaseType)
        ? 'true'
        : 'false',

    [AssignmentField.fromName]: '',
    [AssignmentField.fromAddress]: '',
    [AssignmentField.fromCity]: '',
    [AssignmentField.fromZip]: '',
    [AssignmentField.fromCounty]: '',

    [AssignmentField.toName]: '',
    [AssignmentField.toAddress]: '',
    [AssignmentField.toCity]: '',
    [AssignmentField.toZip]: '',
    [AssignmentField.toCounty]: '',

    [AssignmentField.bookedTo]: '',
    [AssignmentField.startTime]: '',
    [AssignmentField.endTime]: '',
    [AssignmentField.fixedAssignmentTimeInHours]: '',
    // Uthyrd personal: ett arbetspass defaultar till 8h 30m arbetstid + 30 min rast. Inte
    // Samåkning — ett samåkningsben är oftast kort och räknas själv som rast, så standardvärdet
    // blev en onödig rast som fick rättas i efterhand. Samåkningens tider kommer från bäraren.
    [AssignmentField.breakTime]:
      currentCaseType === CaseTypeEnum.RentedStaff ? '30' : '',
    [AssignmentField.extraInfo]: '',

    [AssignmentField.assignedTo]: '',

    // Uthyrd personal: 8h 30m arbetstid (i millisekunder) som default.
    [AssignmentField.estimatedDuration]:
      currentCaseType === CaseTypeEnum.RentedStaff
        ? String(8.5 * 60 * 60 * 1000)
        : '',
    [AssignmentField.estimatedLoadingTime]: '',
    [AssignmentField.estimatedUnloadingTime]: '',
    [AssignmentField.estimatedStartTime]: '',
    [AssignmentField.systemEstimatedDurationExact]: '',
    [AssignmentField.systemEstimatedDurationApprox]: '',

    [AssignmentField.expectedExpenses]: '', // `${Number(ExpenseType.Gas)}`,

    [AssignmentField.standardizedComments]: '[]',

    [AssignmentField.allowPrivateStopOver]: 'false',
    [AssignmentField.keyHandedIn]: '',
    [AssignmentField.shouldGoOnTruck]: 'false',
    [AssignmentField.hasMultiPurchaseAssignmentSiblingsWithEstimatedDurationAndStartTime]:
      'false',
    [AssignmentField.assignedToRating]: '',
    [AssignmentField.assignedToRatingReason]: '',
    [AssignmentField.standByAssignmentTimeInHours]: '',
    [AssignmentField.standByAssignmentIsEligible]: '',
    [AssignmentField.standByAssignmentTimeReason]: '',
    [AssignmentField.assigneeShouldBookTheirOwnTicket]: 'false',
    [AssignmentField.ticketInfo]: '',
    [AssignmentField.firstPossibleStartTime]: '',
    [AssignmentField.lastPossibleEndTime]: '',
    [AssignmentField.fromAddressIsOutSideHomeArea]: 'false',
    [AssignmentField.toAddressIsOutSideHomeArea]: 'false',
    [AssignmentField.isNewAssignment]: 'true',
    [AssignmentField.isUsersFirstAssignmentOfDay]: 'false',
    [AssignmentField.isUsersLastAssignmentOfDay]: 'false',
    [AssignmentField.isUserBetweenAssignments]: 'false',
    [AssignmentField.stayOverTypeID]: '',
    [AssignmentField.stayOverInfo]: '',
    [AssignmentField.ridesAlongWithAssignmentID]: '',
    [AssignmentField.ridesAlongExemptionReason]: '',
    [AssignmentField.runnerCustomerLegOverrideReason]: '',
    [AssignmentField.bookedToChangeReason]: '',
    [AssignmentField.bookedToChangeComment]: '',
    [AssignmentField.originalBookedTo]: '',
    [AssignmentField.ridesAlongRequired]:
      currentCaseType === CaseTypeEnum.Samakning ? 'true' : 'false',
    [AssignmentField.manualElectricCarBufferTime]: '',

    // stopovers
    [AssignmentField.addingStopOver]: 'false',
    [AssignmentField.stopOverName]: '',
    [AssignmentField.stopOverAddress]: '',
    [AssignmentField.stopOverCity]: '',
    [AssignmentField.stopOverZip]: '',
    [AssignmentField.stopOverCounty]: '',
    [AssignmentField.stopOverEstimatedDuration]: '',
    [AssignmentField.stopOverFixedAssignmentTimeInHours]: '',

    // personal transportation to assignment
    [AssignmentField.addingPersonalTransportationToAssignment]: 'false',
    [AssignmentField.personalTransportationToAssignmentEstimatedDuration]: '',
    [AssignmentField.personalTransportationToAssignmentEstimatedStartTime]: '',
    [AssignmentField.personalTransportationToAssignmentFixedAssignmentTimeInHours]:
      '',
    [AssignmentField.personalTransportationToAssignmentName]: '',
    [AssignmentField.personalTransportationToAssignmentAddress]: '',
    [AssignmentField.personalTransportationToAssignmentCity]: '',
    [AssignmentField.personalTransportationToAssignmentZip]: '',
    [AssignmentField.personalTransportationToAssignmentCounty]: '',
    [AssignmentField.personalTransportationToAssignmentAssignmentTypeID]:
      AssignmentTypeEnum.GoByTrain.toString(),
    [AssignmentField.personalTransportationToAssigneeShouldBookTheirOwnTicket]:
      'false',
    [AssignmentField.personalTransportationToTicketInfo]: '',
    [AssignmentField.personalTransportationToExpectedExpenses]: '',

    // personal transportation from assignment
    [AssignmentField.addingPersonalTransportationFromAssignment]: 'false',
    [AssignmentField.personalTransportationFromAssignmentEstimatedDuration]: '',
    [AssignmentField.personalTransportationFromAssignmentEstimatedStartTime]:
      '',
    [AssignmentField.personalTransportationFromAssignmentFixedAssignmentTimeInHours]:
      '',
    [AssignmentField.personalTransportationFromAssignmentName]: '',
    [AssignmentField.personalTransportationFromAssignmentAddress]: '',
    [AssignmentField.personalTransportationFromAssignmentCity]: '',
    [AssignmentField.personalTransportationFromAssignmentZip]: '',
    [AssignmentField.personalTransportationFromAssignmentCounty]: '',
    [AssignmentField.personalTransportationFromAssignmentAssignmentTypeID]:
      AssignmentTypeEnum.GoByTrain.toString(),
    [AssignmentField.personalTransportationFromAssigneeShouldBookTheirOwnTicket]:
      'false',
    [AssignmentField.personalTransportationFromTicketInfo]: '',
    [AssignmentField.personalTransportationFromExpectedExpenses]: '',
  };
}

export function assignmentFormToAssignment(
  base: AssignmentModel | undefined,
  assignmentForm: BasicFormState<AssignmentField>,
  usersById: {
    [key: string]: UserModel;
  },
  zipCodeAreadById: {
    [id: string]: ZipCodeArea;
  },
  // Valfri (se AssignmentFormConversionContext): utan den får currentAssignment bara
  // persisterade koordinater och persisterad laddbuffert, och 10 km-regeln kan inte
  // räknas medan man redigerar ett osparat uppdrag.
  context?: AssignmentFormConversionContext,
  // Ärendetypen, för att känna igen samåkning: dess rast tas — som på en persontransport — under
  // resan och läggs aldrig ovanpå den estimerade tiden. Utan den känns bara persontransporter igen.
  // Egen parameter och inte en del av context: spar-vägen skickar medvetet ingen context.
  caseType?: CaseTypeEnum
): AssignmentModel {
  const assignedToId = assignmentForm[AssignmentField.assignedTo];
  const sendBookedToChangeReason =
    isBookedToChanged(assignmentForm) &&
    bookedToChangeReasonRequired(assignmentForm[AssignmentField.assignmentTypeID]);
  let parsed: StandardizedCommentDto[] = [];
  try {
    parsed = JSON.parse(
      assignmentForm[AssignmentField.standardizedComments] ?? '[]'
    );
  } catch (error) {
    // do nothing
  }

  const standardizedComments = parsed.reduce(
    (acc: StandardizedCommentDto[], cur: StandardizedCommentDto) => {
      return [
        ...acc,
        {
          standardizedCommentId: cur.standardizedCommentId,
          displayName: cur.displayName ?? '',
          comment: cur.comment,
          created: new Date(cur.created),
        } satisfies StandardizedCommentDto,
      ];
    },
    [] as StandardizedCommentDto[]
  );

  // Koordinaterna är server-ägda (geokodning) och har inga formulärfält, men laddbuffertens
  // 10 km-regel behöver dem MEDAN man redigerar. Två källor, i samma ordning som serverns
  // AssignmentCoordinateResolver:
  //   1. Geotaggen på det aktiva företag som formulärets namn+adress+postnr+ort pekar på
  //      (resolverns steg 1). Gäller även osparade uppdrag och nyss bytta adresser.
  //   2. Basmodellens persisterade koordinat, men BARA så länge adressen är oförändrad i
  //      formuläret — speglar serverns nollning + omgeokodning i UpdateCaseAssignment. En
  //      stale koordinat på en ny adress skulle annars kunna visa "ingen buffert" för en
  //      lång resa.
  // Fritextadresser och ogeotaggade företag ger ingen koordinat (resolverns steg 2–3 kräver
  // serveranrop) — då gäller länsregeln tills servern räknat om vid sparning. Hellre en
  // buffert för mycket än släppt på gissning.
  const companyFromCoordinates = context?.resolveCompanyCoordinates?.(
    assignmentForm[AssignmentField.fromName],
    assignmentForm[AssignmentField.fromAddress],
    assignmentForm[AssignmentField.fromZip],
    assignmentForm[AssignmentField.fromCity]
  );
  const companyToCoordinates = context?.resolveCompanyCoordinates?.(
    assignmentForm[AssignmentField.toName],
    assignmentForm[AssignmentField.toAddress],
    assignmentForm[AssignmentField.toZip],
    assignmentForm[AssignmentField.toCity]
  );
  const fromAddressUnchanged =
    !!base &&
    base.fromAddress === assignmentForm[AssignmentField.fromAddress] &&
    base.fromZip === assignmentForm[AssignmentField.fromZip] &&
    base.fromCity === assignmentForm[AssignmentField.fromCity];
  const toAddressUnchanged =
    !!base &&
    base.toAddress === assignmentForm[AssignmentField.toAddress] &&
    base.toZip === assignmentForm[AssignmentField.toZip] &&
    base.toCity === assignmentForm[AssignmentField.toCity];

  const fromCoordinates = companyFromCoordinates ?? {
    latitude: fromAddressUnchanged ? base?.fromLatitude : undefined,
    longitude: fromAddressUnchanged ? base?.fromLongitude : undefined,
  };
  const toCoordinates = companyToCoordinates ?? {
    latitude: toAddressUnchanged ? base?.toLatitude : undefined,
    longitude: toAddressUnchanged ? base?.toLongitude : undefined,
  };

  // Laddbufferten är server-ägd i databasen, men formulärets HÄRLEDDA tider läser den
  // härifrån (useEstimatedWorkTime, derivedDuration → körtidspolicy-anropen). Med bara
  // det persisterade värdet visade de fel så fort något bufferten beror på ändrades i
  // formuläret — och ingenting alls på ett osparat uppdrag. Räkna därför om den på
  // formulärets egna värden när kontexten finns; det är klientens bästa gissning på vad
  // servern skriver vid sparning.
  //
  // Koordinaterna gör att omräkningen inte blir sämre informerad än servern för ett
  // sparat, oförändrat uppdrag: de persisterade koordinaterna följer med ovan (oavsett om
  // servern fick dem från en geotagg eller HERE), så 10 km-regeln ger samma svar här.
  // Saknas de helt (nytt uppdrag med fritextadress, eller en legacy-rad utan koordinater)
  // faller både klient och server tillbaka på länsregeln.
  const manualElectricCarBufferTime =
    assignmentForm[AssignmentField.manualElectricCarBufferTime] === '' ||
    assignmentForm[AssignmentField.manualElectricCarBufferTime] === undefined
      ? undefined
      : Number(assignmentForm[AssignmentField.manualElectricCarBufferTime]);
  // Det sparade värdet kan vara TL-valt, så det gäller så länge inget som regeln beror på har
  // ändrats (samma avgörande som servern gör i ElectricCarBufferHelper.ResolveBufferMs).
  const ruleInputsUnchanged =
    !!base &&
    base.assignmentID > 0 &&
    base.electricCarBufferTime != null &&
    context?.savedIsElectricCase === context?.isElectricCase &&
    fromAddressUnchanged &&
    toAddressUnchanged &&
    base.assignmentTypeID ===
      Number(assignmentForm[AssignmentField.assignmentTypeID]) &&
    base.fromCounty?.id ===
      zipCodeAreadById[assignmentForm[AssignmentField.fromCounty]]?.id &&
    base.toCounty?.id ===
      zipCodeAreadById[assignmentForm[AssignmentField.toCounty]]?.id;
  const electricCarBufferTime = context
    ? getElectricCarBufferMs(
        !!context.isElectricCase,
        assignmentForm[AssignmentField.assignmentTypeID]
          ? Number(assignmentForm[AssignmentField.assignmentTypeID])
          : undefined,
        zipCodeAreadById[assignmentForm[AssignmentField.fromCounty]]?.id,
        zipCodeAreadById[assignmentForm[AssignmentField.toCounty]]?.id,
        {
          fromLatitude: fromCoordinates.latitude,
          fromLongitude: fromCoordinates.longitude,
          toLatitude: toCoordinates.latitude,
          toLongitude: toCoordinates.longitude,
        },
        manualElectricCarBufferTime ??
          (ruleInputsUnchanged ? base?.electricCarBufferTime : undefined)
      )
    : // Spar-vägen: TL:s val, annars ekas det sparade värdet (servern räknar om regeln om
      // adress/län/typ ändrats). Nya uppdrag skickar inget så att servern räknar själv.
      (manualElectricCarBufferTime ?? base?.electricCarBufferTime);

  return {
    caseID: base?.caseID || -1,
    assignmentID: base?.assignmentID || -1,
    publicToken: base?.publicToken,
    assignmentVehicleFaultDiscovered: base?.assignmentVehicleFaultDiscovered,
    assignmentDeviationID: Number(
      assignmentForm[AssignmentField.assignmentDeviationID]
    ),
    assignmentCancelTypeID:
      assignmentForm[AssignmentField.assignmentCancelTypeID] === ''
        ? undefined
        : Number(assignmentForm[AssignmentField.assignmentCancelTypeID]),
    assignmentTypeID: Number(assignmentForm[AssignmentField.assignmentTypeID]),
    sendCustomerUpdateMessages:
      assignmentForm[AssignmentField.sendCustomerUpdateMessages] === 'true',
    timestamp: base?.timestamp || '',
    assignedTo: usersById[assignedToId],

    assignmentStatusID: Number(
      assignmentForm[AssignmentField.assignmentStatusID]
    ),

    fromName: assignmentForm[AssignmentField.fromName],
    fromAddress: assignmentForm[AssignmentField.fromAddress],
    fromCity: assignmentForm[AssignmentField.fromCity],
    fromZip: assignmentForm[AssignmentField.fromZip],
    fromCounty:
      zipCodeAreadById[assignmentForm[AssignmentField.fromCounty]] ?? undefined,

    toName: assignmentForm[AssignmentField.toName],
    toAddress: assignmentForm[AssignmentField.toAddress],
    toCity: assignmentForm[AssignmentField.toCity],
    toZip: assignmentForm[AssignmentField.toZip],
    toCounty:
      zipCodeAreadById[assignmentForm[AssignmentField.toCounty]] ?? undefined,

    bookedTo: new Date(assignmentForm[AssignmentField.bookedTo]),
    startTime: assignmentForm[AssignmentField.startTime]
      ? new Date(assignmentForm[AssignmentField.startTime])
      : undefined,
    endTime: assignmentForm[AssignmentField.endTime]
      ? new Date(assignmentForm[AssignmentField.endTime])
      : undefined,
    fixedAssignmentTime:
      assignmentForm[AssignmentField.fixedAssignmentTimeInHours] === '' ||
      assignmentForm[AssignmentField.fixedAssignmentTimeInHours] === '0'
        ? undefined
        : Math.floor(
            Number(assignmentForm[AssignmentField.fixedAssignmentTimeInHours]) *
              hourInMilliseconds
          ),
    standByAssignmentTime:
      assignmentForm[AssignmentField.standByAssignmentTimeInHours] === ''
        ? undefined
        : Math.floor(
            Number(
              assignmentForm[AssignmentField.standByAssignmentTimeInHours]
            ) * hourInMilliseconds
          ),
    isStandbyEligible:
      assignmentForm[AssignmentField.standByAssignmentIsEligible] === 'true'
        ? true
        : assignmentForm[AssignmentField.standByAssignmentIsEligible] ===
            'false'
          ? false
          : undefined,
    breakTime: Number(assignmentForm[AssignmentField.breakTime]),
    // Redigeras inte i formuläret. Passera igenom befintligt värde — false är reserverat för
    // avgodkännande-reseten; nya uppdrag (base undefined) får backend-defaulten true. Den gamla
    // formulär-coercionen flippade null → false vid varje spara.
    autoApprove: base?.autoApprove,
    extraInfo: assignmentForm[AssignmentField.extraInfo],
    inspectionApproved: base?.inspectionApproved ?? undefined,

    assignmentTimeDisplay: base?.assignmentTimeDisplay ?? '',
    workedTimeDisplay: base?.workedTimeDisplay ?? '',
    expenseSum: base?.expenseSum ?? 0,

    // The picker holds the pure DRIVE estimate; from the planning rollout date onward we store the occupied
    // window (drive + break) for regular assignments so scheduling/overlap/timeline reserve the break.
    // Before the rollout (by BookedTo) the break is NOT added (legacy planning). Fixed-time's EstimatedDuration
    // is recomputed server-side from fixedAssignmentTime + break, so the break is only added here for regular.
    // Passenger legs never get it: the break is taken during the trip.
    estimatedDuration: (() => {
      const drive =
        Math.floor(Number(assignmentForm[AssignmentField.estimatedDuration])) ||
        0;
      if (drive <= 0) {
        return drive;
      }
      const isFixed =
        assignmentForm[AssignmentField.fixedAssignmentTimeInHours] !== '' &&
        assignmentForm[AssignmentField.fixedAssignmentTimeInHours] !== '0';
      const breakMs = Math.round(
        Number(assignmentForm[AssignmentField.breakTime]) * 60 * 1000
      );
      const breakOnTop =
        !isFixed &&
        isBreakOnTopForLeg(
          assignmentForm[AssignmentField.bookedTo],
          Number(
            assignmentForm[AssignmentField.assignmentTypeID]
          ) as AssignmentTypeEnum,
          caseType
        );
      // Efter start är estimatet den reserverade tiden som standby-tiden bygger på. Ändras bara
      // rasten (körtiden är densamma som vid inläsning) behålls det sparade värdet — annars flyttar
      // en rastrad i efterhand den reserverade tiden (uppdrag 954646: 7:00 → 7:30).
      if (
        breakOnTop &&
        base?.startTime &&
        base.estimatedDuration !== undefined &&
        base.estimatedDuration !== null
      ) {
        const loadedDrive = Math.max(
          0,
          base.estimatedDuration - Math.round((base.breakTime ?? 0) * 60 * 1000)
        );
        if (drive === loadedDrive) {
          return base.estimatedDuration;
        }
      }
      return drive + (breakOnTop ? breakMs : 0);
    })(),
    estimatedLoadingTime:
      Math.floor(
        Number(assignmentForm[AssignmentField.estimatedLoadingTime])
      ) ?? undefined,
    estimatedUnloadingTime:
      Math.floor(
        Number(assignmentForm[AssignmentField.estimatedUnloadingTime])
      ) ?? undefined,
    // TL-redigerbart via dropdownen bredvid estimerad tid. Se electricCarBufferTime ovan:
    // live-uträknat när formuläret skickar in kontext, annars valet eller basmodellens värde.
    electricCarBufferTime,
    // Se fromCoordinates/toCoordinates ovan för källordningen.
    fromLatitude: fromCoordinates.latitude,
    fromLongitude: fromCoordinates.longitude,
    toLatitude: toCoordinates.latitude,
    toLongitude: toCoordinates.longitude,
    estimatedStartTime:
      assignmentForm[AssignmentField.estimatedStartTime] !== undefined &&
      assignmentForm[AssignmentField.estimatedStartTime].length > 0
        ? new Date(assignmentForm[AssignmentField.estimatedStartTime])
        : undefined,
    systemEstimatedDurationExact:
      Math.floor(
        Number(assignmentForm[AssignmentField.systemEstimatedDurationExact])
      ) ?? undefined,
    systemEstimatedDurationApprox:
      Math.floor(
        Number(assignmentForm[AssignmentField.systemEstimatedDurationApprox])
      ) ?? undefined,

    expectedExpenses: convertStringToExpectedExpensesArray(
      assignmentForm[AssignmentField.expectedExpenses]
    ),
    standardizedComments,
    standardizedCommentSuggestions: [],

    allowPrivateStopOver:
      assignmentForm[AssignmentField.allowPrivateStopOver] === 'true',

    keyHandedIn:
      assignmentForm[AssignmentField.keyHandedIn] === 'true'
        ? true
        : assignmentForm[AssignmentField.keyHandedIn] === 'false'
          ? false
          : undefined,

    shouldGoOnTruck: assignmentForm[AssignmentField.shouldGoOnTruck] === 'true',

    assignedToRating:
      assignmentForm[AssignmentField.assignedToRating] !== ''
        ? Number(assignmentForm[AssignmentField.assignedToRating])
        : undefined,

    assignedToRatingReason:
      assignmentForm[AssignmentField.assignedToRatingReason] !== ''
        ? assignmentForm[AssignmentField.assignedToRatingReason]
        : undefined,
    assignmentRequests: base?.assignmentRequests ?? [],
    circleKExpenseSum: base?.circleKExpenseSum ?? 0,
    drivingPolicyAssignmentInNeedOfHandling:
      base?.drivingPolicyAssignmentInNeedOfHandling ?? false,
    standbyAssignmentTimeReason:
      assignmentForm[AssignmentField.standByAssignmentTimeReason] ?? '',
    modifiedEstimatedDuration: new Date(),
    modifiedEstimatedDurationByID: -1,

    assigneeShouldBookTheirOwnTicket:
      assignmentForm[AssignmentField.assigneeShouldBookTheirOwnTicket] ===
      'true',
    ticketInfo: assignmentForm[AssignmentField.ticketInfo] ?? '',
    loadedByTransport: base?.loadedByTransport ?? [],
    transportTruckAssignments: base?.transportTruckAssignments ?? [],
    stayOverTypeID:
      assignmentForm[AssignmentField.stayOverTypeID] === ''
        ? undefined
        : Number(assignmentForm[AssignmentField.stayOverTypeID]),
    stayOverInfo: assignmentForm[AssignmentField.stayOverInfo] ?? '',
    ridesAlongWithAssignmentID:
      Number(assignmentForm[AssignmentField.ridesAlongWithAssignmentID]) ||
      undefined,
    ridesAlongExemptionReason:
      assignmentForm[AssignmentField.ridesAlongWithAssignmentID] === 'none'
        ? assignmentForm[AssignmentField.ridesAlongExemptionReason]?.trim() ||
          undefined
        : undefined,
    runnerCustomerLegOverrideReason:
      assignmentForm[AssignmentField.runnerCustomerLegOverrideReason]?.trim() ||
      undefined,
    // Skickas bara med när tiden faktiskt flyttats — backend läser den bara då ändå — och bara
    // för huvuduppdrag. Annars är fälten dolda, och ett val gjort innan typen byttes ska inte
    // följa med. Inte `Number(...) || undefined`: Kundens önskemål har värdet 0.
    bookedToChangeReason:
      sendBookedToChangeReason &&
      assignmentForm[AssignmentField.bookedToChangeReason] !== ''
        ? Number(assignmentForm[AssignmentField.bookedToChangeReason])
        : undefined,
    bookedToChangeComment: sendBookedToChangeReason
      ? assignmentForm[AssignmentField.bookedToChangeComment]?.trim() ||
        undefined
      : undefined,
    localAssignmentID: base?.localAssignmentID ?? undefined,
    loadedByTransportCount: base?.loadedByTransportCount ?? 0,
    invoiceExtras: base?.invoiceExtras ?? [],
  } as AssignmentModel;
}

// Övernattningen hör till ett enda ben per fälttestare och dag, och den förväntade hotellutgiften
// följer natten: benet som lämnar ifrån sig natten ska inte vänta på ett hotellkvitto som hamnar på
// ett annat ben. Backend märker hotellutgiften på ett ben med Hotell som nattens och rensar den om
// Hotell tas bort (CaseLogic.AdjustExpectedExpenses).
const hotelExpenses = (
  expectedExpenses: ExpectedExpenseDto[] | undefined
): ExpectedExpenseDto[] =>
  (expectedExpenses ?? []).filter((e) => e.type === ExpenseType.Hotel);

const withoutHotelExpense = (
  expectedExpenses: ExpectedExpenseDto[] | undefined
): ExpectedExpenseDto[] =>
  (expectedExpenses ?? []).filter((e) => e.type !== ExpenseType.Hotel);

export function assignmentFormToAssignments(
  base: AssignmentModel | undefined,
  assignmentForm: BasicFormState<AssignmentField>,
  usersById: {
    [key: string]: UserModel;
  },
  zipCodeAreadById: {
    [id: string]: ZipCodeArea;
  },
  caseType: CaseTypeEnum
): AssignmentModel[] {
  const originalAssignment = assignmentFormToAssignment(
    base,
    assignmentForm,
    usersById,
    zipCodeAreadById,
    undefined,
    caseType
  );

  let result: AssignmentModel[] = [originalAssignment];

  if (assignmentForm[AssignmentField.addingStopOver] === 'true') {
    // get the stopover with addres
    const stopOverFixedAssignmentTime =
      assignmentForm[AssignmentField.stopOverFixedAssignmentTimeInHours] !== ''
        ? Number(
            assignmentForm[AssignmentField.stopOverFixedAssignmentTimeInHours]
          ) * hourInMilliseconds
        : undefined;

    const stopOverEstimatedDuration =
      assignmentForm[AssignmentField.stopOverEstimatedDuration] !== ''
        ? Math.floor(
            Number(assignmentForm[AssignmentField.stopOverEstimatedDuration])
          )
        : undefined;

    // Formulärets två tidsfält sett från UI:t: "Estimerad tid till mellanlandning" ligger i
    // radens estimatedDuration (start→mellanlandning) och "Estimerad tid från mellanlandning" i
    // stopOverEstimatedDuration (mellanlandning→slutmål). Vilket av benen som är originalet
    // skiljer sig mellan grenarna nedan, så tiderna mappas per rutt — inte per "nytt/original".
    const startToStopOverDrive =
      assignmentForm[AssignmentField.estimatedDuration] !== ''
        ? Math.floor(Number(assignmentForm[AssignmentField.estimatedDuration]))
        : undefined;

    const stopoverShouldComeAfterOriginalAssignment =
      (
        [
          CaseTypeEnum.Purchase,
          CaseTypeEnum.TradeIn,
          CaseTypeEnum.InternalDelivery,
          CaseTypeEnum.Auction,
        ] as CaseTypeEnum[]
      ).includes(caseType) || isRmOtherLikeCaseType(caseType);
    if (stopoverShouldComeAfterOriginalAssignment) {
      const stopOverToEnd: AssignmentModel = {
        ...originalAssignment,
        assignmentID: -1,
        fromName: assignmentForm[AssignmentField.stopOverName],
        fromAddress: assignmentForm[AssignmentField.stopOverAddress],
        fromCity: assignmentForm[AssignmentField.stopOverCity],
        fromZip: assignmentForm[AssignmentField.stopOverZip],
        fromCounty:
          zipCodeAreadById[assignmentForm[AssignmentField.stopOverCounty]] ??
          undefined,
        // Från-sidan är mellanlandningen — spreaden ovan skulle annars ärva
        // originalbenets startkoordinat och para ihop den med en helt annan adress.
        // Servern geokodar den nya adressen; tills dess är okänt rätt svar (koordinat
        // saknas ⇒ laddbuffertens 10 km-regel faller tillbaka på länsregeln).
        fromLatitude: undefined,
        fromLongitude: undefined,
        toName: originalAssignment.toName,
        toAddress: originalAssignment.toAddress,
        toCity: originalAssignment.toCity,
        toZip: originalAssignment.toZip,
        toCounty: originalAssignment.toCounty,
        // Till-sidan är oförändrad (slutdestinationen) — koordinaten följer med.
        toLatitude: originalAssignment.toLatitude,
        toLongitude: originalAssignment.toLongitude,
        // Fast tid sätts av backend för båda benen vid split — se blocket efter if/else.
        fixedAssignmentTime: undefined,
        estimatedDuration: stopOverEstimatedDuration,
        localAssignmentID: `${originalAssignment.localAssignmentID ?? 'noid'}-stopovertoend`,
        electricCarBufferTime: undefined,
        assignedTo: undefined, // no assignee for stopover
        // Benet utan fälttestare är ingen samåkning — bärarlänken hör till fälttestarens ben.
        ridesAlongWithAssignmentID: undefined,
        ridesAlongExemptionReason: undefined,
        // Övernattningen stannar hos fälttestaren på originalbenet.
        stayOverTypeID: undefined,
        stayOverInfo: undefined,
        expectedExpenses: withoutHotelExpense(
          originalAssignment.expectedExpenses
        ),
        // Don't include these fields from the original assignment
        breakTime: 0,
        extraInfo: '',
        standardizedComments: [],
        standardizedCommentSuggestions: [],
        sendCustomerUpdateMessages: false,
        assignmentStatusID: AssignmentStatusEnum.Created,

        startTime: undefined, // start and end time cannot already be set for new assignments
        endTime: undefined, // start and end time cannot already be set for new assignments
      };

      // adjust the original assignment address
      originalAssignment.toName = assignmentForm[AssignmentField.stopOverName];
      originalAssignment.toAddress =
        assignmentForm[AssignmentField.stopOverAddress];
      originalAssignment.toCity = assignmentForm[AssignmentField.stopOverCity];
      originalAssignment.toZip = assignmentForm[AssignmentField.stopOverZip];
      originalAssignment.toCounty =
        zipCodeAreadById[
          Number(assignmentForm[AssignmentField.stopOverCounty])
        ] ?? undefined;
      // Destinationen är nu mellanlandningen — den persisterade koordinaten pekar på
      // slutdestinationen och hör inte längre till till-adressen.
      originalAssignment.toLatitude = undefined;
      originalAssignment.toLongitude = undefined;

      // adjust the stopover estimated start time and booked to
      if (originalAssignment.estimatedStartTime) {
        stopOverToEnd.estimatedStartTime = new Date(
          originalAssignment.estimatedStartTime.getTime() +
            (originalAssignment.fixedAssignmentTime ||
              originalAssignment.estimatedDuration ||
              0)
        );
      }

      if (originalAssignment.bookedTo) {
        stopOverToEnd.bookedTo = new Date(
          originalAssignment.bookedTo.getTime() +
            (originalAssignment.fixedAssignmentTime ||
              originalAssignment.estimatedDuration ||
              0)
        );
      }

      // adjust the assignment types so that the stopover is a PlannedFurtherTransport and the original remains the same
      stopOverToEnd.assignmentTypeID =
        AssignmentTypeEnum.PlannedFurtherTransport;

      stopOverToEnd.sendCustomerUpdateMessages = false; // no customer update message should be sent for the stopover for purchases

      result = [originalAssignment, stopOverToEnd];
    } else {
      // the stopover should lead up to the original assignment
      const stopOverToAssignment: AssignmentModel = {
        ...originalAssignment,
        assignmentID: -1,
        fromName: originalAssignment.fromName,
        fromAddress: originalAssignment.fromAddress,
        fromCity: originalAssignment.fromCity,
        fromZip: originalAssignment.fromZip,
        fromCounty: originalAssignment.fromCounty,
        // Från-sidan är oförändrad (originalbenets startpunkt) — koordinaten följer med.
        fromLatitude: originalAssignment.fromLatitude,
        fromLongitude: originalAssignment.fromLongitude,
        toName: assignmentForm[AssignmentField.stopOverName],
        toAddress: assignmentForm[AssignmentField.stopOverAddress],
        toCity: assignmentForm[AssignmentField.stopOverCity],
        toZip: assignmentForm[AssignmentField.stopOverZip],
        toCounty:
          zipCodeAreadById[assignmentForm[AssignmentField.stopOverCounty]] ??
          undefined,
        // Till-sidan är mellanlandningen — släpp originalbenets destinationskoordinat.
        toLatitude: undefined,
        toLongitude: undefined,
        // Fast tid sätts av backend för båda benen vid split — se blocket efter if/else.
        fixedAssignmentTime: undefined,
        // Det nya benet ÄR start→mellanlandning, så det får "Estimerad tid till mellanlandning".
        // T.o.m. 2026-09 fick det stopOverEstimatedDuration (mellanlandning→slutmål) — tiderna låg
        // korsade mot etiketterna (QA av PR #581, ärende 599134).
        estimatedDuration: startToStopOverDrive,
        localAssignmentID: `${originalAssignment.localAssignmentID ?? 'noid'}-stopovertoassignment`,
        // Eget ben med egen sträcka — servern räknar laddbufferten, inte originalbenets val.
        electricCarBufferTime: undefined,
        // Övernattningen och hotellutgiften följer fälttestaren hit (spreaden ovan).
        // Don't include these fields from the original assignment
        breakTime: 0,
        extraInfo: '',
        standardizedComments: [],
        standardizedCommentSuggestions: [],
        sendCustomerUpdateMessages: false,
        // the stopover leg inherits the assignee (spread above), so the status must
        // follow the assignee — otherwise the FT's leg is left as Created ("Skapat")
        assignmentStatusID: originalAssignment.assignedTo
          ? AssignmentStatusEnum.Planned
          : AssignmentStatusEnum.Created,

        startTime: undefined, // start and end time cannot already be set for new assignments
        endTime: undefined, // start and end time cannot already be set for new assignments
      };

      originalAssignment.assignedTo = undefined; // no assignee for the original assignment
      // Övernattningen och hotellutgiften följde med fälttestaren till det nya benet.
      originalAssignment.stayOverTypeID = undefined;
      originalAssignment.stayOverInfo = undefined;
      originalAssignment.expectedExpenses = withoutHotelExpense(
        originalAssignment.expectedExpenses
      );
      // Bärarlänken följer fälttestaren till det nya benet.
      originalAssignment.ridesAlongWithAssignmentID = undefined;
      originalAssignment.ridesAlongExemptionReason = undefined;
      // the original just lost its assignee — a Planned/Assigned status would otherwise
      // linger on an unassigned leg, so reset it to Created (mirrors deselecting the FT
      // in the assignment form)
      if (
        originalAssignment.assignmentStatusID ===
          AssignmentStatusEnum.Planned ||
        originalAssignment.assignmentStatusID === AssignmentStatusEnum.Assigned
      ) {
        originalAssignment.assignmentStatusID = AssignmentStatusEnum.Created;
      }
      // adjust the original assignment address
      originalAssignment.fromName = stopOverToAssignment.toName;
      originalAssignment.fromAddress = stopOverToAssignment.toAddress;
      originalAssignment.fromCity = stopOverToAssignment.toCity;
      originalAssignment.fromZip = stopOverToAssignment.toZip;
      originalAssignment.fromCounty = stopOverToAssignment.toCounty;
      // Startpunkten är nu mellanlandningen — den persisterade koordinaten hör till
      // den gamla från-adressen.
      originalAssignment.fromLatitude = undefined;
      originalAssignment.fromLongitude = undefined;

      // Originalet är nu mellanlandning→slutmål och får "Estimerad tid från mellanlandning".
      // Rasten läggs ovanpå enligt samma regel som assignmentFormToAssignment (fast tid på den
      // rutten ⇒ ingen rast ovanpå, servern håller fönstret), fast med DEN rutten fast tid som
      // grund. Originalets rast följer med benet (det nya benet har breakTime 0).
      const originalBreakMs = Math.round(
        Number(assignmentForm[AssignmentField.breakTime]) * 60 * 1000
      );
      const originalBreakOnTop =
        stopOverFixedAssignmentTime === undefined &&
        isBreakOnTopForLeg(
          assignmentForm[AssignmentField.bookedTo],
          Number(
            assignmentForm[AssignmentField.assignmentTypeID]
          ) as AssignmentTypeEnum,
          caseType
        );
      originalAssignment.estimatedDuration =
        stopOverEstimatedDuration && stopOverEstimatedDuration > 0
          ? stopOverEstimatedDuration +
            (originalBreakOnTop ? originalBreakMs : 0)
          : 0;

      // Det nya benet slutar där originalet börjar: backa originalets estimerade start och
      // bokade tid med start→mellanlandningens längd. Fast tid från formuläret (radens fält) gäller
      // före estimatet — värdet läses innan fast tid nollas efter if/else.
      const startToStopOverDuration =
        originalAssignment.fixedAssignmentTime || startToStopOverDrive || 0;
      if (originalAssignment.estimatedStartTime) {
        stopOverToAssignment.estimatedStartTime = new Date(
          originalAssignment.estimatedStartTime.getTime() -
            startToStopOverDuration
        );
      }

      if (originalAssignment.bookedTo) {
        stopOverToAssignment.bookedTo = new Date(
          originalAssignment.bookedTo.getTime() - startToStopOverDuration
        );
      }

      // adjust the assignment types so that the stopover is a PlannedFurtherTransport and the original remains the same
      stopOverToAssignment.assignmentTypeID =
        AssignmentTypeEnum.PlannedFurtherTransport;

      stopOverToAssignment.sendCustomerUpdateMessages = false; // no customer update message should be sent for the stopover for

      result = [stopOverToAssignment, originalAssignment];
    }

    // Fast tid vid split räknas av backend, inte av formuläret. Formulärets fält
    // (fixedAssignmentTimeInHours = start→mellanlandning, stopOverFixedAssignmentTimeInHours =
    // mellanlandning→slutmål) beskriver rutterna sett från UI:t, men vilket ben som är "originalet"
    // och vilket som är det nya skiljer sig mellan grenarna ovan. T.o.m. 2026-09 skickades
    // stopOver-värdet på det nya benet oavsett gren, så i före-grenen fick kund→hubb-benet
    // hubb→slutmålets prisrad (uppdrag 952509: 4,67 h Sundsvall↔Strängnäs på Öjebyn→Sundsvall).
    // Backend kunde inte rätta det: nya ben med fritextadress lämnas orörda i
    // CaseLogic.UpdateCaseAssignment (isNew-undantaget), eftersom ett inskickat värde där annars
    // inte går att skilja från en manuell inmatning.
    //
    // Skickas null på båda benen räknar CaseLogic.UpdateCaseAssignment fram rätt värde ur
    // CompanyLogic.CalculateFixedAssignmentTime: originalbenet för att en adress ändrats, det nya
    // benet för att båda adresserna matchar företag. Matchar de inte finns ingen fast tid, och
    // null är då också rätt. Offsetberäkningarna ovan har redan läst formulärvärdena.
    originalAssignment.fixedAssignmentTime = undefined;
  }

  // check if we also need to add a personal transportation leading up to the first assignment
  if (
    assignmentForm[AssignmentField.addingPersonalTransportationToAssignment] ===
    'true'
  ) {
    // inject the personal transportation to assignment before the other two assignments
    const firstAssignment = result[0];
    const personalTransportationToAssignment: AssignmentModel = {
      ...firstAssignment,
      assignmentID: -1,
      // Persontransporten är ett eget ben med egen sträcka, ingen samåkning — ärv inte bärarlänken.
      ridesAlongWithAssignmentID: undefined,
      ridesAlongExemptionReason: undefined,
      // Övernattningen hör till dagens sista ben — ärv den inte (se från-benet nedan).
      stayOverTypeID: undefined,
      stayOverInfo: undefined,
      // if assigned to has a value, set assignment status to Planned, otherwise keep it Created
      assignmentStatusID: firstAssignment.assignedTo
        ? AssignmentStatusEnum.Planned
        : AssignmentStatusEnum.Created,
      assignmentTypeID: Number(
        assignmentForm[
          AssignmentField.personalTransportationToAssignmentAssignmentTypeID
        ]
      ) as AssignmentTypeEnum,
      fromName:
        assignmentForm[AssignmentField.personalTransportationToAssignmentName],
      fromAddress:
        assignmentForm[
          AssignmentField.personalTransportationToAssignmentAddress
        ],
      fromCity:
        assignmentForm[AssignmentField.personalTransportationToAssignmentCity],
      fromZip:
        assignmentForm[AssignmentField.personalTransportationToAssignmentZip],
      fromCounty:
        zipCodeAreadById[
          assignmentForm[
            AssignmentField.personalTransportationToAssignmentCounty
          ]
        ] ?? undefined,
      // Nytt ben med egna adresser — ärv inte firstAssignments koordinater via spreaden.
      // Från-sidan geokodas av servern; till-sidan ÄR firstAssignments startpunkt.
      fromLatitude: undefined,
      fromLongitude: undefined,
      toName: firstAssignment.fromName,
      toAddress: firstAssignment.fromAddress,
      toCity: firstAssignment.fromCity,
      toZip: firstAssignment.fromZip,
      toCounty: firstAssignment.fromCounty,
      toLatitude: firstAssignment.fromLatitude,
      toLongitude: firstAssignment.fromLongitude,
      fixedAssignmentTime: assignmentForm[
        AssignmentField
          .personalTransportationToAssignmentFixedAssignmentTimeInHours
      ]
        ? Number(
            assignmentForm[
              AssignmentField
                .personalTransportationToAssignmentFixedAssignmentTimeInHours
            ]
          ) * hourInMilliseconds
        : undefined,
      estimatedDuration:
        assignmentForm[
          AssignmentField.personalTransportationToAssignmentEstimatedDuration
        ] !== ''
          ? Math.floor(
              Number(
                assignmentForm[
                  AssignmentField
                    .personalTransportationToAssignmentEstimatedDuration
                ]
              )
            )
          : undefined,
      localAssignmentID: `${originalAssignment.localAssignmentID ?? 'noid'}-personaltransporttoassignment`,
      electricCarBufferTime: undefined,
      assigneeShouldBookTheirOwnTicket:
        assignmentForm[
          AssignmentField
            .personalTransportationToAssigneeShouldBookTheirOwnTicket
        ] === 'true',
      ticketInfo:
        assignmentForm[AssignmentField.personalTransportationToTicketInfo] ??
        '',
      expectedExpenses: convertStringToExpectedExpensesArray(
        assignmentForm[AssignmentField.personalTransportationToExpectedExpenses]
      ),
      estimatedStartTime: new Date(
        assignmentForm[
          AssignmentField.personalTransportationToAssignmentEstimatedStartTime
        ]
      ),
      bookedTo: assignmentForm[
        AssignmentField.personalTransportationToAssignmentEstimatedStartTime
      ]
        ? new Date(
            assignmentForm[
              AssignmentField.personalTransportationToAssignmentEstimatedStartTime
            ]
          )
        : new Date(
            assignmentForm[AssignmentField.bookedTo]
              ? assignmentForm[AssignmentField.bookedTo]
              : firstAssignment.bookedTo
          ),
      // Don't include these fields from the original assignment
      breakTime: 0,
      extraInfo: '',
      standardizedComments: [],
      standardizedCommentSuggestions: [],
      sendCustomerUpdateMessages: false,

      startTime: undefined, // start and end time cannot already be set for new assignments
      endTime: undefined, // start and end time cannot already be set for new assignments
    };

    // // adjust the estimated start time and booked to of the personal transportation
    // if (firstAssignment.estimatedStartTime) {
    //   personalTransportationToAssignment.estimatedStartTime = new Date(
    //     firstAssignment.estimatedStartTime.getTime() -
    //       (personalTransportationToAssignment.fixedAssignmentTime ||
    //         personalTransportationToAssignment.estimatedDuration ||
    //         0)
    //   );
    // }

    // if (firstAssignment.bookedTo) {
    //   personalTransportationToAssignment.bookedTo = new Date(
    //     firstAssignment.bookedTo.getTime() -
    //       (personalTransportationToAssignment.fixedAssignmentTime ||
    //         personalTransportationToAssignment.estimatedDuration ||
    //         0)
    //   );
    // }

    result.unshift(personalTransportationToAssignment);
  }

  if (
    assignmentForm[
      AssignmentField.addingPersonalTransportationFromAssignment
    ] === 'true'
  ) {
    // inject the personal transportation from assignment after the other two assignments
    const lastAssignment = result[result.length - 1];
    const personalTransportationFromAssignment: AssignmentModel = {
      ...lastAssignment,
      assignmentID: -1,
      // Persontransporten är ett eget ben med egen sträcka, ingen samåkning — ärv inte bärarlänken.
      ridesAlongWithAssignmentID: undefined,
      ridesAlongExemptionReason: undefined,
      // Ärv inte övernattningen. Bokat nästa morgon fick benet förr en förväntad hotellutgift
      // för en natt som redan låg på kvällsbenet (41 fastnade autogodkännanden aug–okt 2026,
      // ex. 968376). Bokat samma dag flyttas övernattningen hit nedan.
      stayOverTypeID: undefined,
      stayOverInfo: undefined,
      // if assigned to has a value, set assignment status to Planned, otherwise keep it Created
      assignmentStatusID: lastAssignment.assignedTo
        ? AssignmentStatusEnum.Planned
        : AssignmentStatusEnum.Created,
      assignmentTypeID: Number(
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentAssignmentTypeID
        ]
      ) as AssignmentTypeEnum,
      fromName: lastAssignment.toName,
      fromAddress: lastAssignment.toAddress,
      fromCity: lastAssignment.toCity,
      fromZip: lastAssignment.toZip,
      fromCounty: lastAssignment.toCounty,
      // Från-sidan ÄR lastAssignments destination; till-sidan är en ny adress som
      // servern geokodar (ärv inte lastAssignments destinationskoordinat via spreaden).
      fromLatitude: lastAssignment.toLatitude,
      fromLongitude: lastAssignment.toLongitude,
      toName:
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentName
        ],
      toAddress:
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentAddress
        ],
      toCity:
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentCity
        ],
      toZip:
        assignmentForm[AssignmentField.personalTransportationFromAssignmentZip],
      toCounty:
        zipCodeAreadById[
          assignmentForm[
            AssignmentField.personalTransportationFromAssignmentCounty
          ]
        ] ?? undefined,
      toLatitude: undefined,
      toLongitude: undefined,
      fixedAssignmentTime: assignmentForm[
        AssignmentField
          .personalTransportationFromAssignmentFixedAssignmentTimeInHours
      ]
        ? Number(
            assignmentForm[
              AssignmentField
                .personalTransportationFromAssignmentFixedAssignmentTimeInHours
            ]
          ) * hourInMilliseconds
        : undefined,
      estimatedDuration:
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentEstimatedDuration
        ] !== ''
          ? Math.floor(
              Number(
                assignmentForm[
                  AssignmentField
                    .personalTransportationFromAssignmentEstimatedDuration
                ]
              )
            )
          : undefined,
      localAssignmentID: `${lastAssignment.localAssignmentID}-personaltransportfromassignment`,
      electricCarBufferTime: undefined,
      assigneeShouldBookTheirOwnTicket:
        assignmentForm[
          AssignmentField
            .personalTransportationFromAssigneeShouldBookTheirOwnTicket
        ] === 'true',
      ticketInfo:
        assignmentForm[AssignmentField.personalTransportationFromTicketInfo] ??
        '',
      expectedExpenses: convertStringToExpectedExpensesArray(
        assignmentForm[
          AssignmentField.personalTransportationFromExpectedExpenses
        ]
      ),
      estimatedStartTime: new Date(
        assignmentForm[
          AssignmentField.personalTransportationFromAssignmentEstimatedStartTime
        ]
      ),
      bookedTo: assignmentForm[
        AssignmentField.personalTransportationFromAssignmentEstimatedStartTime
      ]
        ? new Date(
            assignmentForm[
              AssignmentField.personalTransportationFromAssignmentEstimatedStartTime
            ]
          )
        : new Date(
            assignmentForm[AssignmentField.bookedTo]
              ? assignmentForm[AssignmentField.bookedTo]
              : lastAssignment.bookedTo
          ),
      // Don't include these fields from the original assignment
      breakTime: 0,
      extraInfo: '',
      standardizedComments: [],
      standardizedCommentSuggestions: [],
      sendCustomerUpdateMessages: false,
      startTime: undefined, // start and end time cannot already be set for new assignments
      endTime: undefined, // start and end time cannot already be set for new assignments
    };

    // // adjust the estimated start time and booked to of the personal transportation
    // if (lastAssignment.estimatedStartTime) {
    //   personalTransportationFromAssignment.estimatedStartTime = new Date(
    //     lastAssignment.estimatedStartTime.getTime() +
    //       (lastAssignment.fixedAssignmentTime ||
    //         lastAssignment.estimatedDuration ||
    //         0)
    //   );
    // }
    // if (lastAssignment.bookedTo) {
    //   personalTransportationFromAssignment.bookedTo = new Date(
    //     lastAssignment.bookedTo.getTime() +
    //       (lastAssignment.fixedAssignmentTime ||
    //         lastAssignment.estimatedDuration ||
    //         0)
    //   );
    // }
    // Samma dag är persontransporten dagens sista ben och bär natten: ruttplaneringen och
    // dagsplaneringen läser övernattningen från dagens sista ben. Kvar på uppdragsbenet blev den
    // mitt på dagen (och två hotellnätter samma dag stoppas av hotellspärren).
    // Ett påbörjat uppdragsben behåller natten: hotellkvittot kan redan ligga där, och spärren
    // undantar påbörjade uppdrag på samma sätt.
    if (
      lastAssignment.stayOverTypeID !== undefined &&
      lastAssignment.stayOverTypeID !== null &&
      lastAssignment.assignmentStatusID < AssignmentStatusEnum.Started &&
      isSameSwedishDay(
        personalTransportationFromAssignment.bookedTo,
        lastAssignment.bookedTo
      )
    ) {
      personalTransportationFromAssignment.stayOverTypeID =
        lastAssignment.stayOverTypeID;
      personalTransportationFromAssignment.stayOverInfo =
        lastAssignment.stayOverInfo;
      // Ett Hotell som TL kryssat i på persontransportbenet behålls; annars följer uppdragsbenets med.
      const ownHotelExpenses = hotelExpenses(
        personalTransportationFromAssignment.expectedExpenses
      );
      personalTransportationFromAssignment.expectedExpenses = [
        ...(personalTransportationFromAssignment.expectedExpenses ?? []),
        ...(ownHotelExpenses.length > 0
          ? []
          : hotelExpenses(lastAssignment.expectedExpenses)),
      ];
      lastAssignment.stayOverTypeID = undefined;
      lastAssignment.stayOverInfo = undefined;
      lastAssignment.expectedExpenses = withoutHotelExpense(
        lastAssignment.expectedExpenses
      );
    }
    // if purchase, then set the send messages to same as original
    result.push(personalTransportationFromAssignment);
  }

  return result;
}

// export

export interface CaseAssignmentFormState {
  [assignmentId: string]: BasicFormState<AssignmentField>;
}
interface CaseAssignmentFormValidations {
  [assignmentId: string]: ValidationResults<AssignmentField>;
}
interface CaseAssignmentForm {
  state: CaseAssignmentFormState;
  validations: CaseAssignmentFormValidations;
  onChange(assignmentId: number, field: AssignmentField, value: string): void;
  /** if null, start creating new asignment */
  startEdit(
    assignment: AssignmentModel | null,
    caseType: CaseTypeEnum,
    defaultAssignmentType?: AssignmentTypeEnum
  ): void;
  cancelEdit(assignmentId: number): void;
  cancelAllEdits(): void;
}

const useCaseAssignmentForms = (): CaseAssignmentForm => {
  const [state, setState] = useState<{
    fieldState: CaseAssignmentFormState;
    validations: CaseAssignmentFormValidations;
  }>({
    fieldState: {},
    validations: {},
  });

  const handleChange = useCallback(
    (assignmentId: number, field: AssignmentField, value: string) => {
      setState((s) => {
        const newAssignmentState: BasicFormState<AssignmentField> = {
          ...s.fieldState[assignmentId],
          [field]: value,
        };

        // When "Ska ha standby-tid" is set to "Nej", clear any leftover
        // standby time so it doesn't block setting a break (the backend
        // rejects break time longer than standby time).
        if (
          field === AssignmentField.standByAssignmentIsEligible &&
          value === 'false'
        ) {
          newAssignmentState[AssignmentField.standByAssignmentTimeInHours] = '';
          newAssignmentState[AssignmentField.standByAssignmentTimeReason] = '';
        }

        return {
          ...s,
          fieldState: {
            ...s.fieldState,
            [assignmentId]: newAssignmentState,
          },
          validations: {
            ...s.validations,
            [assignmentId]: createValidations(newAssignmentState),
          },
        };
      });
    },
    []
  );

  const handleStartEdit = useCallback(
    (
      assignment: AssignmentModel | null,
      caseType: CaseTypeEnum,
      defaultAssignmentType?: AssignmentTypeEnum
    ) => {
      const initialAssignmentFormState = initializeAssignmentForm(
        assignment,
        caseType,
        defaultAssignmentType
      );

      setState((s) => ({
        ...s,
        fieldState: {
          ...s.fieldState,
          [assignment?.assignmentID ?? -1]: initialAssignmentFormState,
        },
        validations: {
          ...s.validations,
          [assignment?.assignmentID ?? -1]: createValidations(
            initialAssignmentFormState
          ),
        },
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleCancelEdit = useCallback((assignmentId: number) => {
    setState((s) => {
      const newState: typeof state = {
        ...s,
        fieldState: { ...s.fieldState },
        validations: { ...s.validations },
      };
      delete newState.fieldState[assignmentId];
      delete newState.validations[assignmentId];
      return newState;
    });
  }, []);

  const handleCancelAllEdits = useCallback(() => {
    setState((s) => ({ ...s, fieldState: {}, validations: {} }));
  }, []);

  return useMemo(
    (): CaseAssignmentForm => ({
      state: state.fieldState,
      validations: state.validations,
      onChange: handleChange,
      startEdit: handleStartEdit,
      cancelEdit: handleCancelEdit,
      cancelAllEdits: handleCancelAllEdits,
    }),
    [
      state,
      handleCancelAllEdits,
      handleCancelEdit,
      handleChange,
      handleStartEdit,
    ]
  );
};

export const travelsBetweenDifferentCounties = (
  countyIdA?: string,
  countyIdB?: string
): boolean => !!countyIdA && !!countyIdB && countyIdA !== countyIdB;

export const getExpectedExpensesBasedOnSelfTransportation = (
  assignmentTypeID: AssignmentTypeEnum,
  travelsBetweenCounties?: boolean
): ExpenseType | undefined => {
  switch (assignmentTypeID) {
    case AssignmentTypeEnum.GoByBoat:
      return ExpenseType.BoatTicket;
    case AssignmentTypeEnum.GoByPlane:
      return ExpenseType.FlightTicket;
    case AssignmentTypeEnum.GoByTrain:
      return ExpenseType.SJTicket;
    case AssignmentTypeEnum.GoByBus:
      return ExpenseType.SLTicket;
    case AssignmentTypeEnum.GoByPublicTransport:
      // Kollektivtrafik mellan två olika län sker i praktiken med SJ snarare än
      // lokaltrafik — defaulta då till SJ-biljett så att förväntad utgift inte
      // fastnar i autogodkännandets maxbelopp för lokaltrafik.
      return travelsBetweenCounties
        ? ExpenseType.SJTicket
        : ExpenseType.SLTicket;
    default:
      return undefined;
  }
};

export const fieldTesterShouldBookTheirOwnTicketByDefault = (
  assignmentTypeID: AssignmentTypeEnum
): boolean => {
  switch (assignmentTypeID) {
    case AssignmentTypeEnum.GoByTrain:
    case AssignmentTypeEnum.GoByBus:
    case AssignmentTypeEnum.GoByPublicTransport:
      return true;
    default:
      return false;
  }
};

export const getUnExceptedExpensesBasedOnSelfTransportation = (
  assignmentTypeID: AssignmentTypeEnum,
  travelsBetweenCounties?: boolean
): ExpenseType[] => {
  const transportationExpenseTypes = [
    ExpenseType.DirectBooking,
    ExpenseType.FlightTicket,
    ExpenseType.SJTicket,
    ExpenseType.SLTicket,
    ExpenseType.BoatTicket,
  ];
  const expectedExpense = getExpectedExpensesBasedOnSelfTransportation(
    assignmentTypeID,
    travelsBetweenCounties
  );
  if (expectedExpense === undefined) {
    return [];
  }
  return transportationExpenseTypes.filter((x) => x !== expectedExpense);
};

export default useCaseAssignmentForms;
